/**
 * The practice page's half of spec 53 5.3, steps 4 and 5: carry a signed
 * receipt from the practice fleet back to production, and keep it until it
 * lands.
 *
 * Unsent receipts live in sessionStorage under `lw:training`, key `receipts`;
 * the modules passed in this tab under key `passed`. stepLog.js owns the
 * `run` key of the same object, and each side leaves the other's keys as it
 * found them. Storage can be unavailable; then nothing survives a reload,
 * and a lost receipt costs a retry of the module, never a wrong record.
 */
const KEY = 'lw:training';
const MAX_PENDING = 20;
export const DEFAULT_ACCOUNT_ORIGIN = 'https://app.ladeway.com';

function readAll() {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    const saved = raw ? JSON.parse(raw) : null;
    return saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {};
  } catch (e) {
    return {};
  }
}

function writeAll(all) {
  try {
    if (Object.keys(all).length) window.sessionStorage.setItem(KEY, JSON.stringify(all));
    else window.sessionStorage.removeItem(KEY);
  } catch (e) {
    // Kept for this page only.
  }
}

/** Receipts not yet accepted by production: [{ receipt, module }]. */
export function pendingReceipts() {
  const list = readAll().receipts;
  return Array.isArray(list)
    ? list.filter(r => r && typeof r.receipt === 'string' && r.receipt)
    : [];
}

export function addPending(entry) {
  const all = readAll();
  const list = pendingReceipts().filter(r => r.receipt !== entry.receipt);
  list.push({ receipt: entry.receipt, module: entry.module || null });
  all.receipts = list.slice(-MAX_PENDING);
  writeAll(all);
}

export function removePending(receipt) {
  const all = readAll();
  const list = pendingReceipts().filter(r => r.receipt !== receipt);
  if (list.length) all.receipts = list;
  else delete all.receipts;
  writeAll(all);
}

/** Module ids passed in this tab (the practice-side list's ticks). */
export function passedHere() {
  const list = readAll().passed;
  return Array.isArray(list) ? list.filter(id => typeof id === 'string') : [];
}

/** Announced on window when a pass is remembered, so an open list ticks it. */
export const PASSED_EVENT = 'lw:training-passed';

export function rememberPassed(moduleId) {
  if (typeof moduleId !== 'string' || !moduleId) return;
  const all = readAll();
  all.passed = [...new Set([...passedHere(), moduleId])];
  writeAll(all);
  window.dispatchEvent(new window.CustomEvent(PASSED_EVENT, { detail: { module: moduleId } }));
}

/**
 * A run's captured ids as the practice server reads them (WP3's
 * practice/complete: `captured: {load_id, driver_id}`): the definitions
 * capture in camelCase (`loadId`), the server names columns.
 */
export function capturedForServer(captured) {
  const out = {};
  for (const [key, value] of Object.entries(captured || {})) {
    out[key.replace(/[A-Z]/g, c => `_${c.toLowerCase()}`)] = value;
  }
  return out;
}

/**
 * Production's origin, where receipts go and where "Go to my account" points:
 * the training lease's `return_origin` when it names one plainly (scheme and
 * host, nothing else), app.ladeway.com otherwise.
 */
export function accountOrigin(lease) {
  const value = lease && typeof lease.return_origin === 'string' ? lease.return_origin.trim() : '';
  const plain = value.replace(/\/+$/, '');
  return /^https?:\/\/[^/\s?#]+$/.test(plain) ? plain : DEFAULT_ACCOUNT_ORIGIN;
}

/**
 * Post one receipt to production's POST /api/training/receipts. No
 * credentials and no session: the practice fleet's signature is its whole
 * authority (TrainingController.receipt). Answers production's
 * { module_id, passed, cleared, cleared_at, outstanding, duplicate }, or
 * throws an Error with `status` (0 when it never arrived) and `reason`.
 */
export async function sendReceipt(receipt, origin, fetchImpl = window.fetch.bind(window)) {
  let res;
  try {
    res = await fetchImpl(`${origin}/api/training/receipts`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ receipt }),
    });
  } catch (err) {
    const failure = new Error('The receipt did not reach your account.');
    failure.status = 0;
    throw failure;
  }
  let body = null;
  try {
    body = await res.json();
  } catch (err) {
    body = null;
  }
  if (!res.ok) {
    const failure = new Error((body && body.detail) || 'Your account refused the receipt.');
    failure.status = res.status;
    failure.reason = body && body.reason;
    throw failure;
  }
  return body || {};
}

/**
 * Whether a refusal means the receipt will never be accepted (so keeping it
 * would only retry forever): production said it is invalid. Anything else
 * (offline, a 5xx, the rate limit) is worth another try.
 */
export function isFinalRefusal(err) {
  return Boolean(err && err.status === 400);
}
